import {
  add,
  addNum,
  isExactNum,
  isIntegerNum,
  mul,
  mulNum,
  type Node,
  type NumNode,
  negNum,
  num,
  numValue,
  pow,
  variable,
  ZERO,
} from "@/lib/tool-kit/calculus/ast";

/**
 * Univariate polynomials with exact rational coefficients — just enough to cancel a
 * common factor between the numerator and denominator of a derivative, so that
 * (4x − 4)/(x − 1)⁴ reads 4/(x − 1)³.
 *
 * A polynomial is the array of its coefficients, index = degree, with no trailing
 * zeros; the zero polynomial is the empty array.
 */

export type Poly = NumNode[];

const MAX_DEGREE = 64;

function trim(p: Poly): Poly {
  const out = [...p];
  while (out.length > 0 && numValue(out.at(-1) ?? ZERO) === 0) {
    out.pop();
  }
  return out;
}

export function polyAdd(a: Poly, b: Poly): Poly {
  const out: Poly = [];
  for (let k = 0; k < Math.max(a.length, b.length); k += 1) {
    out.push(addNum(a[k] ?? ZERO, b[k] ?? ZERO));
  }
  return trim(out);
}

export function polyMul(a: Poly, b: Poly): Poly | null {
  if (a.length === 0 || b.length === 0) {
    return [];
  }
  if (a.length + b.length - 1 > MAX_DEGREE + 1) {
    return null;
  }
  const out: Poly = Array.from({ length: a.length + b.length - 1 }, () => ZERO);
  for (let i = 0; i < a.length; i += 1) {
    for (let j = 0; j < b.length; j += 1) {
      out[i + j] = addNum(out[i + j], mulNum(a[i], b[j]));
    }
  }
  return trim(out);
}

/** The polynomial a node denotes in `v`, or null if it is anything else. */
export function polyFromNode(node: Node, v: string): Poly | null {
  switch (node.kind) {
    case "num":
      return isExactNum(node) ? trim([node]) : null;
    case "var":
      return node.name === v ? [ZERO, num(1)] : null;
    case "add":
      return foldPolys(node.terms, v, [], (x, y) => polyAdd(x, y));
    case "mul":
      return foldPolys(node.factors, v, [num(1)], polyMul);
    case "pow": {
      const base = polyFromNode(node.base, v);
      if (!(base && isIntegerNum(node.exp)) || node.exp.n < 0) {
        return null;
      }
      let acc: Poly | null = [num(1)];
      for (let i = 0; i < node.exp.n && acc; i += 1) {
        acc = polyMul(acc, base);
      }
      return acc;
    }
    default:
      return null;
  }
}

function foldPolys(
  nodes: Node[],
  v: string,
  initial: Poly,
  combine: (a: Poly, b: Poly) => Poly | null
): Poly | null {
  let acc: Poly | null = initial;
  for (const node of nodes) {
    const poly = polyFromNode(node, v);
    if (poly === null || acc === null) {
      return null;
    }
    acc = combine(acc, poly);
  }
  return acc;
}

/** Exact division: quotient and remainder, or null for a zero divisor. */
export function polyDivide(
  a: Poly,
  b: Poly
): { quotient: Poly; remainder: Poly } | null {
  if (b.length === 0) {
    return null;
  }
  let remainder = [...a];
  const quotient: Poly = Array.from(
    { length: Math.max(0, a.length - b.length + 1) },
    () => ZERO
  );
  const lead = b.at(-1) ?? ZERO;
  const inverseLead = num(lead.d, lead.n);
  while (remainder.length >= b.length) {
    const shift = remainder.length - b.length;
    const factor = mulNum(remainder.at(-1) ?? ZERO, inverseLead);
    quotient[shift] = factor;
    for (let i = 0; i < b.length; i += 1) {
      remainder[shift + i] = addNum(
        remainder[shift + i],
        negNum(mulNum(factor, b[i]))
      );
    }
    remainder = trim(remainder.slice(0, remainder.length - 1));
    // The leading term cancels exactly; drop it even if rounding left a trace.
  }
  return { quotient: trim(quotient), remainder: trim(remainder) };
}

/** An unsimplified sum, highest degree first. */
export function polyToNode(p: Poly, v: string): Node {
  if (p.length === 0) {
    return ZERO;
  }
  const terms: Node[] = [];
  for (let k = p.length - 1; k >= 0; k -= 1) {
    if (numValue(p[k]) === 0) {
      continue;
    }
    if (k === 0) {
      terms.push(p[k]);
    } else {
      const power = k === 1 ? variable(v) : pow(variable(v), num(k));
      terms.push(mul(p[k], power));
    }
  }
  return terms.length === 1 ? terms[0] : add(...terms);
}
